import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  TextField, Box, CircularProgress, Alert,
} from '@mui/material';
import { Description, Summarize } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import useAtsSenderName from '../../hooks/useAtsSenderName';

/**
 * Envío al cliente de las fichas (CV + resumen ejecutivo) de los candidatos
 * seleccionados del shortlist. Un solo correo con todos. El email del cliente se
 * ingresa manualmente (sin prefill). Se adjunta lo disponible; el resumen se
 * chequea aquí (aviso previo) y el backend confirma qué faltó tras el envío.
 * El envío real lo hace el padre vía onConfirm.
 */
const AtsSendToClientDialog = ({
  open,
  onClose,
  applications = [],
  positionName = '',
  orgName = '',
  onConfirm,
}) => {
  const { t } = useTranslation();
  const { authToken, project, organization } = useStore.getState();

  const [clientEmail, setClientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [infoByApp, setInfoByApp] = useState({});
  const [loadingInfo, setLoadingInfo] = useState(false);
  const { senderName, setSenderName } = useAtsSenderName(open);

  useEffect(() => {
    if (!open) return;
    setClientEmail('');
    setSending(false);
    loadTemplate();
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Resuelve {{cargo}} y {{empresa}}; deja {{candidatos}} para que lo complete el backend.
  const resolveVars = (text) =>
    String(text || '')
      .replace(/\{\{\s*cargo\s*\}\}/g, positionName || '')
      .replace(/\{\{\s*empresa\s*\}\}/g, orgName || '');

  // Carga la plantilla configurada (Configuración > correo al cliente); si falla, usa defaults i18n.
  const loadTemplate = async () => {
    let subj = t('ats.sendClient.defaultSubject', { position: positionName || '' });
    let bod = t('ats.sendClient.defaultBody', { position: positionName || '', company: orgName || '' });
    try {
      if (organization && project) {
        const res = await api.get(`/projects/ats-shortlist-email`, {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = res.data.response?.data;
        if (data?.subject) subj = data.subject;
        if (data?.body) bod = data.body;
      }
    } catch (e) { /* usa defaults i18n */ }
    setSubject(resolveVars(subj));
    setBody(resolveVars(bod));
  };

  const loadPreview = async () => {
    const ids = applications.map((a) => a.uid || a.id).filter(Boolean);
    if (ids.length === 0) return;
    setLoadingInfo(true);
    try {
      const res = await api.post(
        `/applications/shortlist-preview`,
        { applicationIds: ids },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const list = res.data.response?.data?.candidates || [];
      const map = {};
      list.forEach((c) => {
        map[c.applicationId] = { cvUrl: c.cvUrl || null, resumenUrl: c.resumenUrl || null };
      });
      setInfoByApp(map);
    } catch (e) {
      setInfoByApp({});
    } finally {
      setLoadingInfo(false);
    }
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientEmail.trim());
  const canSend = emailValid && applications.length > 0 && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await onConfirm({ clientEmail: clientEmail.trim(), subject, body, senderName });
    } finally {
      setSending(false);
    }
  };

  const missingResumenCount = applications.filter((a) => !infoByApp[a.uid || a.id]?.resumenUrl).length;

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('ats.sendClient.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('ats.sendClient.description', { count: applications.length })}
        </Typography>

        <TextField
          fullWidth
          size="small"
          required
          type="email"
          label={t('ats.sendClient.clientEmail')}
          placeholder="cliente@empresa.com"
          value={clientEmail}
          onChange={(e) => setClientEmail(e.target.value)}
          error={!!clientEmail && !emailValid}
          helperText={!!clientEmail && !emailValid ? t('ats.sendClient.invalidEmail') : ''}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          size="small"
          label={t('ats.config.emailSender.fieldLabel')}
          value={senderName}
          onChange={(e) => setSenderName(e.target.value)}
          inputProps={{ maxLength: 80 }}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          size="small"
          label={t('ats.sendClient.subject')}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          multiline
          minRows={4}
          label={t('ats.sendClient.body')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          helperText={t('ats.sendClient.bodyHint')}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2">
            {t('ats.sendClient.candidates')} ({applications.length})
          </Typography>
          {loadingInfo && <CircularProgress size={14} />}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('ats.sendClient.previewHint')}
        </Typography>

        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
          {applications.map((a, idx) => {
            const info = infoByApp[a.uid || a.id] || {};
            return (
              <Box
                key={a.uid || a.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderTop: idx > 0 ? '1px solid #eee' : 'none',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {a.candidateName || a.name || a.candidateEmail || '—'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Description sx={{ fontSize: 15 }} />}
                    disabled={!info.cvUrl}
                    onClick={() => info.cvUrl && window.open(info.cvUrl, '_blank')}
                  >
                    {t('ats.sendClient.viewCv')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Summarize sx={{ fontSize: 15 }} />}
                    disabled={!info.resumenUrl}
                    onClick={() => info.resumenUrl && window.open(info.resumenUrl, '_blank')}
                  >
                    {t('ats.sendClient.viewResumen')}
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          {t('ats.sendClient.attachNote')}
        </Alert>

        {missingResumenCount > 0 && !loadingInfo && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t('ats.sendClient.missingResumenWarning', { count: missingResumenCount })}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={sending}>
          {t('cancel')}
        </Button>
        <Button onClick={handleSend} variant="contained" disabled={!canSend}>
          {sending ? <CircularProgress size={18} color="inherit" /> : t('ats.sendClient.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsSendToClientDialog;
